import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Activity, Eye, MessageSquare, User, Volume2 } from "lucide-react";

/**
 * Detailed view of a past interview session.
 * Shows overall stats, ML analysis, and per-question breakdown with AI feedback.
 */
export const InterviewHistoryDetail = ({ session, onBack }) => {
  if (!session) return null;
  const fb = session.overall_feedback;
  const ml = fb?.ml_analysis;

  return (
    <div className="max-w-4xl mx-auto">
      <Button variant="outline" onClick={onBack} className="mb-4">
        ← Back to History
      </Button>

      <Card className="border-0 shadow-xl bg-white/80 backdrop-blur">
        <CardHeader>
          <CardTitle>{session.target_role || session.career_field}</CardTitle>
          <CardDescription>
            {session.mode?.replace("_", " ")} • {session.industry} •{" "}
            {new Date(session.started_at).toLocaleDateString()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fb && <OverallStatsGrid feedback={fb} />}

          {ml && <MLAnalysisSection ml={ml} />}

          <h3 className="font-semibold text-lg mb-4">Questions & Answers</h3>
          <div className="space-y-4">
            {session.questions?.filter((q) => q.answer || q.feedback).map((q, idx) => (
              <QuestionAnswerCard key={idx} question={q} index={idx} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const OverallStatsGrid = ({ feedback }) => (
  <div className="grid grid-cols-4 gap-4 mb-6">
    <StatCard label="Overall" value={Math.round(feedback.average_score)} color="blue" />
    <StatCard label="Grammar" value={feedback.average_grammar} color="green" />
    <StatCard label="Quality" value={feedback.average_quality} color="purple" />
    <StatCard label="Level" value={feedback.performance_level} color="orange" />
  </div>
);

const StatCard = ({ label, value, color }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-orange-50 text-orange-600",
  };
  return (
    <div className={`text-center p-4 rounded-lg ${colors[color]?.split(" ")[0] || "bg-gray-50"}`}>
      <p className={`text-2xl font-bold ${colors[color]?.split(" ")[1] || "text-gray-600"}`}>{value}</p>
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );
};

const MLAnalysisSection = ({ ml }) => {
  const scoreColor = (val, thresholds = [70, 50]) =>
    val >= thresholds[0] ? "text-green-600" :
    val >= thresholds[1] ? "text-yellow-600" : "text-red-600";
  const paceColor = (val) =>
    val >= 120 && val <= 150 ? "text-green-600" :
    val >= 100 ? "text-yellow-600" : "text-red-600";
  const fillerColor = (val) =>
    val <= 5 ? "text-green-600" : val <= 10 ? "text-yellow-600" : "text-red-600";

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
      <h3 className="font-semibold text-lg text-indigo-900 mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5" />
        Body Language & Voice Analysis
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {ml.body_language?.avg_eye_contact !== undefined && (
          <MLMetric icon={<Eye className="w-4 h-4 text-blue-500" />} label="Eye Contact"
            value={`${ml.body_language.avg_eye_contact}%`}
            colorClass={scoreColor(ml.body_language.avg_eye_contact)} />
        )}
        {ml.body_language?.avg_posture !== undefined && (
          <MLMetric icon={<User className="w-4 h-4 text-purple-500" />} label="Posture"
            value={`${ml.body_language.avg_posture}%`}
            colorClass={scoreColor(ml.body_language.avg_posture)} />
        )}
        {ml.voice?.avg_speaking_pace !== undefined && (
          <MLMetric icon={<Volume2 className="w-4 h-4 text-green-500" />} label="Pace (WPM)"
            value={ml.voice.avg_speaking_pace}
            colorClass={paceColor(ml.voice.avg_speaking_pace)} />
        )}
        {ml.voice?.total_filler_words !== undefined && (
          <MLMetric icon={<MessageSquare className="w-4 h-4 text-orange-500" />} label="Filler Words"
            value={ml.voice.total_filler_words}
            colorClass={fillerColor(ml.voice.total_filler_words)} />
        )}
      </div>

      {ml.specific_feedback?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-indigo-800">Detailed Feedback:</p>
          {ml.specific_feedback.map((feedback, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm text-gray-700 bg-white p-2 rounded border border-gray-100">
              <span className="text-indigo-500 mt-0.5">•</span>
              <span>{feedback}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MLMetric = ({ icon, label, value, colorClass }) => (
  <div className="bg-white p-3 rounded-lg border border-indigo-100">
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <span className="text-xs text-gray-600">{label}</span>
    </div>
    <p className={`text-xl font-bold ${colorClass}`}>{value}</p>
  </div>
);

const QuestionAnswerCard = ({ question: q, index: idx }) => {
  const difficultyColor = {
    easy: "bg-green-100 text-green-800",
    medium: "bg-yellow-100 text-yellow-800",
    hard: "bg-orange-100 text-orange-800",
  }[q.difficulty] || "bg-red-100 text-red-800";

  return (
    <Card className="bg-gray-50">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline">Q{idx + 1}</Badge>
            <Badge className={difficultyColor}>{q.difficulty}</Badge>
          </div>
          {q.feedback && (
            <Badge variant={q.feedback.overall_score >= 70 ? "default" : "secondary"}>
              {q.feedback.overall_score}/100
            </Badge>
          )}
        </div>

        <p className="font-medium text-gray-800 mb-3">{q.question}</p>

        {q.answer && (
          <div className="p-3 bg-white rounded-lg mb-3">
            <p className="text-sm font-medium text-gray-600 mb-1">Your Answer:</p>
            <p className="text-gray-800">{q.answer}</p>
          </div>
        )}

        {q.feedback && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-900 mb-2">AI Feedback:</p>
            {q.feedback.strengths?.length > 0 && (
              <div className="mb-2">
                <p className="text-sm text-green-700 font-medium">✓ Strengths:</p>
                <ul className="text-sm text-gray-700 list-disc list-inside">
                  {q.feedback.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {q.feedback.improvements?.length > 0 && (
              <div className="mb-2">
                <p className="text-sm text-orange-700 font-medium">→ Improvements:</p>
                <ul className="text-sm text-gray-700 list-disc list-inside">
                  {q.feedback.improvements.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {q.feedback.tips && (
              <p className="text-sm text-blue-700">💡 {q.feedback.tips}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InterviewHistoryDetail;
